import { defineComponent as _defineComponent } from 'vue'
import { Ref, computed, onBeforeMount, ref } from 'vue';
import dateformat from 'dateformat';
import store from '@/store';
import { getCustomer, getDefaultPaymentMethod, getInvoices } from '@/api/billing';
import CardIcon from '@/views/Account/components/CardIcon.vue';
import CreditCardInput from '@/views/Account/components/CreditCardInput.vue';
import type { AxiosError } from 'axios';
import type { Customer, Invoice, PaymentMethod } from '@/api/typings/billing';
import { StoreType } from '@/typings/user';


export default /*#__PURE__*/_defineComponent({
  __name: 'PodBilling',
  emits: ['user-updated'],
  setup(__props, { emit }) {



const invoicesPerPage = 10;
const invoiceTableFields = [
    { key: 'date', label: 'Date' },
    { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Amount', class: 'text-center' },
    { key: 'status', label: 'Status' },
    { key: 'invoice', label: 'Invoice', class: 'text-center' }
];

const storeUrl = ref('');
const customer: Ref<Customer | null> = ref(null);
const paymentMethod: Ref<PaymentMethod | null> = ref(null);
const invoices: Ref<Invoice[]> = ref([]);
const loadingPaymentMethod = ref(false);
const loadingInvoices = ref(false);
const errorGettingPaymentMethod = ref(false);
const errorGettingInvoices = ref(false);
const showUpdateCardDialog = ref(false);
const disablePrevBtn = ref(false);
const disableNextBtn = ref(false);

const stripePublicKey = computed(() => store.state.stripePublicKey);

const invoiceItems = computed(() => {
    return invoices.value.map((i) => ({
        date: formatDateString(new Date(i.Created)),
        amount: `$${(i.Total / 100).toFixed(2)}`,
        status: i.Status,
        invoice: i.InvoicePdf
    }));
});

onBeforeMount(async () => {
    try {
        loadingPaymentMethod.value = true;
        loadingInvoices.value = true;
        //If the user has more than one shopify store, all of them will have the same customer, so the first one is taken
        let userStore = store.state.user?.Stores.find((s) => s.Type == StoreType.Shopify && s.HasCustomerId);
        if (!userStore) {
            userStore = store.state.user?.Stores.find((s) => s.Type == StoreType.Shopify);
        }
        storeUrl.value = userStore?.Url || '';

        if (!storeUrl.value) {
            return;
        }

        //Get payment method
        try {
            errorGettingPaymentMethod.value = false;
            customer.value = await getCustomer(storeUrl.value);
            if (!customer.value) {
                return;
            }
            paymentMethod.value = await getDefaultPaymentMethod(storeUrl.value);
        } catch (error) {
            const axiosError = error as AxiosError;
            if (!axiosError || (axiosError && axiosError.request?.status !== 404)) {
                window.rumReportError(axiosError);
                paymentMethod.value = null;
                errorGettingPaymentMethod.value = true;
            }
        }

        //Get invoices
        try {
            const data = await getInvoices(storeUrl.value, invoicesPerPage);
            invoices.value = data.Invoices;
            disableNextBtn.value = false;
            disablePrevBtn.value = true;
            if (!data.MoreAvailable) {
                disableNextBtn.value = true;
            }
        } catch (error) {
            const axiosError = error as AxiosError;
            if (!axiosError || (axiosError && axiosError.request?.status !== 404)) {
                window.rumReportError(axiosError);
                invoices.value = [];
                errorGettingInvoices.value = true;
            }
        }
    } catch (error) {
        window.rumReportError(error as Error);
    } finally {
        loadingPaymentMethod.value = false;
        loadingInvoices.value = false;
    }
});

const formatDateString = (date: Date | string): string => {
    return dateformat(date, 'mmm dd, yyyy');
};

const goPrevInvoicesPage = async () => {
    try {
        loadingInvoices.value = true;
        const data = await getInvoices(storeUrl.value, invoicesPerPage, undefined, invoices.value[0].Id);
        invoices.value = data.Invoices;

        disableNextBtn.value = disablePrevBtn.value = false;
        if (!data.MoreAvailable) {
            disablePrevBtn.value = true;
        }
    } finally {
        loadingInvoices.value = false;
    }
};

const goNextInvoicesPage = async () => {
    try {
        loadingInvoices.value = true;
        const data = await getInvoices(storeUrl.value, invoicesPerPage, invoices.value[invoices.value.length - 1].Id);
        invoices.value = data.Invoices;

        disableNextBtn.value = disablePrevBtn.value = false;
        if (!data.MoreAvailable) {
            disableNextBtn.value = true;
        }
    } finally {
        loadingInvoices.value = false;
    }
};

const editCreditCard = () => {
    showUpdateCardDialog.value = true;
};

const onCardSubmit = async () => {
    showUpdateCardDialog.value = false;
    try {
        loadingPaymentMethod.value = true;
        emit('user-updated');
        try {
            errorGettingPaymentMethod.value = false;
            paymentMethod.value = await getDefaultPaymentMethod(storeUrl.value);
        } catch (error) {
            const axiosError = error as AxiosError;
            if (!axiosError || (axiosError && axiosError.request?.status !== 404)) {
                window.rumReportError(axiosError);
                paymentMethod.value = null;
                errorGettingPaymentMethod.value = true;
            }
        }
        if (!customer.value) {
            //The customer is created when the user adds the credit card.
            customer.value = await getCustomer(storeUrl.value);
        }
    } finally {
        loadingPaymentMethod.value = false;
    }
};

return { __sfc: true,emit, invoicesPerPage, invoiceTableFields, storeUrl, customer, paymentMethod, invoices, loadingPaymentMethod, loadingInvoices, errorGettingPaymentMethod, errorGettingInvoices, showUpdateCardDialog, disablePrevBtn, disableNextBtn, stripePublicKey, invoiceItems, formatDateString, goPrevInvoicesPage, goNextInvoicesPage, editCreditCard, onCardSubmit, CardIcon, CreditCardInput }
}

})